import { DragDrop } from '@/components/drag-drop';
import { useUiPickerObject } from '@/hooks/ui/use-ui-picker-object';
import { ObjectParams } from '@/hooks/use-relation-insert';
import { LinkIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TrashIcon } from '@heroicons/react/24/outline';
import { FieldProps } from '@rjsf/utils';
import clsx from 'clsx';
import { useState } from 'react';
import { useEdges, Handle, Position, useNodeId } from 'reactflow';
import * as Popover from '@radix-ui/react-popover';
import { ObjectPickerForm } from '@/components/object-picker-form';
import { Option } from '@/components/ui/combo-box';

interface RelationHandle {
  id: string;
}

export function RelationPickerField({
  formData,
  name,
  schema,
  onBlur,
  onChange,
  idSchema,
  uiSchema,
}: FieldProps) {
  const { type, filterBy } = uiSchema?.['ui:options'] || {};
  const widget = uiSchema?.['ui:widget'] || null;
  const { data: pickerObjectData } = useUiPickerObject({
    type: type as string,
    uuid: formData?.value,
  });
  const [title, setTitle] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const nodeId = useNodeId();
  const edges = useEdges();

  let hasConnection = false;
  const sources: RelationHandle[] = [
    ...(schema?.sources ?? []),
    ...(formData?.sources ?? []),
  ];
  const targets: RelationHandle[] = [
    ...(schema?.targets ?? []),
    ...(formData?.targets ?? []),
  ];

  function handleDrop({ value }: { value: ObjectParams[] }) {
    onChange({
      attribute: name,
      type: 'absolute',
      value: {
        uuid: value[0].uuid,
        container: value[0].container,
      },
    });
  }

  function handleRemoveClick() {
    onChange({
      attribute: name,
      type: 'absolute',
      value: '',
    });
  }

  function handleSubmit(option: Option | null) {
    onChange({
      attribute: name,
      type: 'absolute',
      value: option?.value,
      label: option?.label,
    });
    setIsOpen(false);
    setTitle(option?.label || '');
  }

  const hasConnectedValue =
    formData?.type === 'value_v2' && formData?.abstractUuid;

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <div className="form-group">
        <label className="control-label">{schema.title}</label>
        <div className="relative">
          {sources.map((handle) => {
            const [type] = handle.id.split('|');

            const className = clsx(
              type === 'attribute' && 'bg-blue-500',
              type === 'attribute' && targets.length === 0 && 'top-1',
              type === 'attribute' && targets.length > 0 && 'top-2',
              type === 'value' && 'bg-orange-300',
              type === 'value' && targets.length === 0 && 'top-4',
              type === 'value' && targets.length > 0 && 'top-9',
              type === 'value_v2' && 'bg-orange-300',
              type === 'value_v2' && targets.length === 0 && 'top-4',
              type === 'value_v2' && targets.length > 0 && 'top-9',
              'after:h-5 after:w-5 -right-[4px] after:absolute h-3 w-3 after:rounded-full after:-translate-x-1/2 after:-translate-y-1/2 after:top-1/2 after:left-1/2'
            );

            return (
              <Handle
                key={handle.id}
                id={handle.id}
                type="source"
                position={Position.Right}
                className={className}
              />
            );
          })}
          {targets.map((handle, i: number) => {
            const [_, parentField, index, childField] = idSchema.$id.split('_');
            const targetId = index
              ? `${parentField}_${childField}_${index}`
              : handle.id;

            hasConnection = !!edges.find((edge) => {
              return edge.targetHandle === targetId && edge.target === nodeId;
            });

            const className = clsx(
              i === 0 && 'top-5 z-10',
              'after:h-5 after:w-5 -left-1.5 after:absolute h-3 w-3 bg-black after:rounded-full after:-translate-x-1/2 after:-translate-y-1/2 after:top-1/2 after:left-1/2'
            );

            return (
              <Handle
                key={idSchema.$id}
                id={targetId}
                type="target"
                position={Position.Left}
                className={className}
              />
            );
          })}
          {hasConnectedValue ? (
            <input
              type="text"
              className="form-control"
              value={formData.abstractUuid}
              readOnly
            />
          ) : widget === 'picker' ? (
            <Popover.Trigger asChild>
              <button className="relative flex items-center space-x-3 rounded-lg border border-gray-300 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-400 border-dashed justify-center text-center h-5 w-full">
                <PlusIcon className="h-4 w-4" />
              </button>
            </Popover.Trigger>
          ) : (
            <button className="w-full">
              <DragDrop
                allowedTypes={['object/insert']}
                onDrop={handleDrop}
                className="relative flex items-center space-x-3 rounded-lg border border-gray-300 bg-purple px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-400 border-dashed justify-center text-center h-5"
                droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-800 border-dashed bg-purple-50 flex justify-center items-center h-5"
              >
                {({ isDropping }) =>
                  isDropping ? (
                    <PlusIcon className="h-4 w-4" />
                  ) : (
                    <PlusIcon className="h-4 w-4" />
                  )
                }
              </DragDrop>
            </button>
          )}

          {!hasConnectedValue && formData?.value && type ? (
            <div className="mt-2 relative flex items-center gap-3 rounded-lg border border-gray-300 bg-white py-2 pl-2 pr-3 shadow-sm text-xs">
              <div className="relative w-10 h-10 bg-purple-50 rounded-full flex justify-center items-center text-center">
                <LinkIcon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0 w-0 basis-0">
                <p className="font-medium text-gray-900">
                  {pickerObjectData?.data?.item?.label || title}
                </p>
                <p className="truncate text-gray-500">{formData.value}</p>
              </div>
              <button
                onClick={handleRemoveClick}
                className="text-gray-400 hover:text-gray-700"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ) : null}
        </div>
      </div>
      {!hasConnectedValue && widget === 'picker' ? (
        <Popover.Portal>
          <Popover.Content
            className="bg-white rounded-lg p-4 shadow-lg"
            sideOffset={5}
            side="top"
          >
            <ObjectPickerForm
              type={type as string}
              filter={filterBy
                ? (item) => Object.entries(filterBy as Record<string, unknown>).every(([k, v]) => (item.data as Record<string, unknown>)?.[k] === v)
                : undefined}
              onSubmit={handleSubmit}
            />
            <Popover.Close
              className="absolute top-2 right-2"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </Popover.Close>
            <Popover.Arrow className="fill-white" />
          </Popover.Content>
        </Popover.Portal>
      ) : null}
    </Popover.Root>
  );
}
